import React, { useState, useEffect } from 'react';
import { Shield, Plus, Trash2, Ban, CheckCircle, Wifi, AlertTriangle, Eye, RefreshCw, MapPin, Globe, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/api';

// ============== TAB: KNOWN IPS ==============
const KnownIPsTab = () => {
  const [ips, setIps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ip_address: '', label: '', venue_name: '' });

  const load = async () => {
    try {
      const res = await api.getKnownIPs();
      setIps(res.data.ips);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await api.addKnownIP(form);
      setForm({ ip_address: '', label: '', venue_name: '' });
      setShowForm(false);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error adding IP');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this known IP?')) return;
    try {
      await api.deleteKnownIP(id);
      load();
    } catch (e) { alert('Error removing IP'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <MapPin className="w-5 h-5 text-amber-400" />
            Known Commercial IPs ({ips.length})
          </h3>
          <p className="text-sm text-gray-400">IPs of pubs, bars, and venues that require a Pub Pass</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="bg-amber-500 hover:bg-amber-600 text-black" size="sm">
          <Plus className="w-4 h-4 mr-1" /> Add IP
        </Button>
      </div>

      {showForm && (
        <Card className="bg-gray-800/50 border-amber-900/30">
          <CardContent className="pt-4">
            <form onSubmit={handleAdd} className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-gray-300 text-xs">IP Address</Label>
                <Input value={form.ip_address} onChange={(e) => setForm({ ...form, ip_address: e.target.value })} placeholder="203.0.113.50" className="bg-gray-700 border-gray-600 text-white" required />
              </div>
              <div>
                <Label className="text-gray-300 text-xs">Venue Name</Label>
                <Input value={form.venue_name} onChange={(e) => setForm({ ...form, venue_name: e.target.value })} placeholder="Ned Devin's Saloon" className="bg-gray-700 border-gray-600 text-white" required />
              </div>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-gray-300 text-xs">Label</Label>
                  <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="NYC - Downtown" className="bg-gray-700 border-gray-600 text-white" required />
                </div>
                <Button type="submit" size="sm" className="bg-amber-500 hover:bg-amber-600 text-black">Save</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="text-center py-8"><Loader2 className="w-6 h-6 text-gray-400 animate-spin mx-auto" /></div>
      ) : ips.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No known IPs registered yet.</div>
      ) : (
        <div className="space-y-2">
          {ips.map(ip => (
            <div key={ip.id} className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 flex items-center justify-between" data-testid={`known-ip-${ip.id}`}>
              <div className="flex items-center gap-3">
                <Wifi className="w-4 h-4 text-amber-400" />
                <div>
                  <span className="text-white font-mono text-sm">{ip.ip_address}</span>
                  <span className="text-gray-400 mx-2">·</span>
                  <span className="text-amber-300 text-sm">{ip.venue_name}</span>
                </div>
                <Badge className="bg-gray-700 text-gray-300 text-xs">{ip.label}</Badge>
              </div>
              <Button size="sm" variant="ghost" onClick={() => handleDelete(ip.id)}>
                <Trash2 className="w-4 h-4 text-red-400" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== TAB: BANS ==============
const BansTab = () => {
  const [data, setData] = useState({ banned_viewers: [], ip_bans: [], recent_violations: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await api.getBans();
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleUnban = async (viewerId) => {
    if (!window.confirm('Reinstate this account? All strikes will be cleared.')) return;
    try {
      await api.unbanViewer(viewerId);
      load();
    } catch (e) { alert('Error unbanning viewer'); }
  };

  const handleUnbanIP = async (ipId) => {
    if (!window.confirm('Unban this IP address?')) return;
    try {
      await api.unbanIP(ipId);
      load();
    } catch (e) { alert('Error unbanning IP'); }
  };

  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  if (loading) return <div className="text-center py-8"><Loader2 className="w-6 h-6 text-gray-400 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-8">
      {/* Banned Accounts */}
      <div>
        <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-3">
          <Ban className="w-5 h-5 text-red-500" />
          Banned Accounts ({data.banned_viewers.length})
        </h3>
        {data.banned_viewers.length === 0 ? (
          <p className="text-gray-500 text-sm py-4">No banned accounts.</p>
        ) : (
          <div className="space-y-2">
            {data.banned_viewers.map(v => (
              <div key={v.id} className="bg-gray-800/50 border border-red-900/30 rounded-lg p-3 flex items-center justify-between" data-testid={`banned-viewer-${v.id}`}>
                <div>
                  <span className="text-white font-medium">{v.name || v.email}</span>
                  <span className="text-gray-500 ml-2 text-sm">{v.email}</span>
                  <div className="text-xs text-gray-500 mt-1">
                    Strikes: {v.strike_count || 0} · Banned: {formatDate(v.banned_at)} · {v.ban_reason}
                  </div>
                </div>
                <Button size="sm" onClick={() => handleUnban(v.id)} className="bg-green-600 hover:bg-green-700">
                  <CheckCircle className="w-4 h-4 mr-1" /> Reinstate
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* IP Bans */}
      <div>
        <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-3">
          <Globe className="w-5 h-5 text-red-500" />
          IP Bans ({data.ip_bans.length})
        </h3>
        {data.ip_bans.length === 0 ? (
          <p className="text-gray-500 text-sm py-4">No IP bans active.</p>
        ) : (
          <div className="space-y-2">
            {data.ip_bans.map(ban => (
              <div key={ban.id} className="bg-gray-800/50 border border-red-900/30 rounded-lg p-3 flex items-center justify-between" data-testid={`ip-ban-${ban.id}`}>
                <div>
                  <span className="text-white font-mono text-sm">{ban.ip_address}</span>
                  <span className="text-gray-500 text-sm ml-2">· {ban.reason}</span>
                  <div className="text-xs text-gray-500 mt-1">Banned: {formatDate(ban.created_at)}</div>
                </div>
                <Button size="sm" onClick={() => handleUnbanIP(ban.id)} className="bg-green-600 hover:bg-green-700">
                  <CheckCircle className="w-4 h-4 mr-1" /> Unban
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Violations */}
      <div>
        <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-3">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          Recent Violations ({data.recent_violations.length})
        </h3>
        {data.recent_violations.length === 0 ? (
          <p className="text-gray-500 text-sm py-4">No violations recorded.</p>
        ) : (
          <div className="max-h-80 overflow-y-auto space-y-2">
            {data.recent_violations.map(v => (
              <div key={v.id} className="bg-gray-800/30 border border-gray-700 rounded-lg p-3 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium">{v.viewer_name || 'Unknown'}</span>
                  <span className="text-gray-500">{v.viewer_email}</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  IP: {v.ip_address} · Venue: {v.venue_name} · {formatDate(v.created_at)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============== TAB: ENVIRONMENT TRACKING ==============
const EnvironmentTab = () => {
  const [suspicious, setSuspicious] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedViewer, setSelectedViewer] = useState(null);

  const load = async () => {
    try {
      const [susRes, logsRes] = await Promise.all([
        api.getSuspiciousViewers(),
        api.getEnvironmentLogs({ suspicious_only: true })
      ]);
      setSuspicious(susRes.data.suspicious_viewers);
      setLogs(logsRes.data.logs);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const loadViewerLogs = async (viewerId) => {
    setSelectedViewer(viewerId);
    try {
      const res = await api.getEnvironmentLogs({ viewer_id: viewerId });
      setLogs(res.data.logs);
    } catch (e) { console.error(e); }
  };

  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  if (loading) return <div className="text-center py-8"><Loader2 className="w-6 h-6 text-gray-400 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-8">
      {/* Suspicious Viewers */}
      <div>
        <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-3">
          <Eye className="w-5 h-5 text-purple-400" />
          Suspicious Viewers ({suspicious.length})
        </h3>
        <p className="text-sm text-gray-400 mb-3">Viewers with frequent IP changes in the last 30 days (potential VPN usage)</p>
        {suspicious.length === 0 ? (
          <p className="text-gray-500 text-sm py-4">No suspicious activity detected.</p>
        ) : (
          <div className="space-y-2">
            {suspicious.map(s => (
              <div
                key={s.viewer_id}
                className={`bg-gray-800/50 border rounded-lg p-3 cursor-pointer hover:border-purple-500/50 transition-colors ${
                  selectedViewer === s.viewer_id ? 'border-purple-500' : 'border-gray-700'
                }`}
                onClick={() => loadViewerLogs(s.viewer_id)}
                data-testid={`suspicious-viewer-${s.viewer_id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-white font-medium">{s.name}</span>
                    <span className="text-gray-500 ml-2 text-sm">{s.email}</span>
                    {s.is_banned && <Badge className="bg-red-500 ml-2">Banned</Badge>}
                  </div>
                  <div className="text-right">
                    <div className="text-purple-400 font-bold">{s.ip_change_count} IP changes</div>
                    <div className="text-xs text-gray-500">{s.unique_ips?.length} unique IPs</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Environment Logs */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-blue-400" />
            IP Change Logs
            {selectedViewer && <Badge className="bg-purple-500/20 text-purple-300">Filtered</Badge>}
          </h3>
          {selectedViewer && (
            <Button size="sm" variant="ghost" onClick={() => { setSelectedViewer(null); load(); }} className="text-gray-400">
              Show All
            </Button>
          )}
        </div>
        {logs.length === 0 ? (
          <p className="text-gray-500 text-sm py-4">No IP change events logged.</p>
        ) : (
          <div className="max-h-96 overflow-y-auto space-y-1">
            {logs.map(log => (
              <div key={log.id} className="bg-gray-800/30 border border-gray-700 rounded p-2 text-xs flex items-center gap-3">
                <span className="text-gray-400 w-32 shrink-0">{formatDate(log.created_at)}</span>
                <span className="text-white">{log.viewer_name || log.viewer_email}</span>
                {log.previous_ip && (
                  <>
                    <span className="text-red-400 font-mono">{log.previous_ip}</span>
                    <span className="text-gray-600">&rarr;</span>
                  </>
                )}
                <span className="text-green-400 font-mono">{log.ip_address}</span>
                <span className="text-gray-600 font-mono text-[10px] truncate">{log.device_fingerprint?.substring(0, 12)}...</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============== MAIN COMPONENT ==============
export const AdminEnforcement = () => {
  const [activeTab, setActiveTab] = useState('ips');

  const tabs = [
    { id: 'ips', label: 'Known IPs', icon: MapPin },
    { id: 'bans', label: 'Bans', icon: Ban },
    { id: 'tracking', label: 'Environment', icon: Eye },
  ];

  return (
    <div className="space-y-6" data-testid="admin-enforcement">
      <div className="flex items-center gap-3 mb-2">
        <Shield className="w-6 h-6 text-sky-400" />
        <h2 className="text-2xl font-bold text-white">Enforcement & Security</h2>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-gray-800/50 p-1 rounded-lg w-fit">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-sky-500 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
              data-testid={`enforcement-tab-${tab.id}`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'ips' && <KnownIPsTab />}
      {activeTab === 'bans' && <BansTab />}
      {activeTab === 'tracking' && <EnvironmentTab />}
    </div>
  );
};
